import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import StageForm from '../components/StageForm';
import { EmptyState, IconBadge, SoftChip } from '../components/ui';
import { DeclineDialog, RemapDialog } from '../components/ReasonDialog';
import { STAGE_FIELDS } from '../constants/stageFields';
import { RT } from '../theme/retentionTheme';
import {
  remapDemoAttempt,
  saveParentDemoFeedback,
  saveTutorFeedback,
  saveTutorResponse,
  scheduleDemo,
} from '../../../redux/features/retentionSlice';
import useDemoData, { openAttempt } from './useDemoData';

// Stages 5 (demo) and 6 (demo feedback), one block per subject's open attempt. The tutor side is on hold, so the CRO
// records the tutor's response (5) and 6a feedback on their behalf. Parent says no → RemapDialog → back to stage 4.

const [TUTOR_SECTION, PARENT_SECTION] = STAGE_FIELDS[6].sections;
const TUTOR_CONFIG = { sections: [TUTOR_SECTION] };
const PARENT_CONFIG = { sections: [PARENT_SECTION] };

function AttemptHeader({ subject, attempt }) {
  return (
    <Stack direction="row" alignItems="center" gap={1.25} mb={1.5}>
      <IconBadge icon="solar:user-speak-bold-duotone" color="#7C3AED" size={34} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {subject.subject_name} · {attempt.trainer_name || 'Tutor'}
        </Typography>
        <Typography variant="caption" sx={{ color: RT.muted }}>
          Attempt {attempt.attempt_no}
          {attempt.demo_datetime ? ` · demo ${dayjs(attempt.demo_datetime).format('DD MMM, hh:mm A')}` : ''}
        </Typography>
      </Box>
    </Stack>
  );
}

function ScheduleBlock({ subject, attempt, saving, run }) {
  const [declining, setDeclining] = useState(false);
  const navigate = useNavigate();
  const responded = attempt.tutor_response !== null && attempt.tutor_response !== undefined;

  const decline = async (reason) => {
    const ok = await run(saveTutorResponse({ attemptId: attempt.retention_demo_attempt_id, accepted: false, reason }), 'Tutor unmapped');
    if (ok) {
      setDeclining(false);
      navigate('../onboarding?stage=4');
    }
  };

  const response = responded ? (
    <SoftChip label="Tutor accepted" icon="solar:check-circle-bold" color={RT.success} />
  ) : (
    <>
      <Button
        size="small"
        variant="outlined"
        color="error"
        disabled={saving}
        onClick={() => setDeclining(true)}
        startIcon={<Icon icon="solar:close-circle-bold" width={16} />}
      >
        Tutor declined
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="success"
        disabled={saving || !attempt.demo_datetime}
        onClick={() => run(saveTutorResponse({ attemptId: attempt.retention_demo_attempt_id, accepted: true }), 'Tutor accepted')}
        startIcon={<Icon icon="solar:check-circle-bold" width={16} />}
      >
        Tutor accepted
      </Button>
    </>
  );

  return (
    <Box>
      <AttemptHeader subject={subject} attempt={attempt} />
      <StageForm
        stageNo={5}
        values={attempt}
        saving={saving}
        saveLabel={attempt.demo_datetime ? 'Update demo' : 'Schedule demo'}
        onSave={(changes) => run(scheduleDemo({ attemptId: attempt.retention_demo_attempt_id, ...changes }), 'Demo scheduled')}
        extraActions={response}
      />
      <DeclineDialog
        open={declining}
        tutorName={attempt.trainer_name}
        busy={saving}
        onClose={() => setDeclining(false)}
        onConfirm={decline}
      />
    </Box>
  );
}

function FeedbackBlock({ subject, attempt, reasons, saving, run }) {
  const [remapping, setRemapping] = useState(false);
  const navigate = useNavigate();
  const tutorDone = Boolean(attempt.tutor_feedback_at);
  const happy = attempt.is_parent_happy === null || attempt.is_parent_happy === undefined ? null : Number(attempt.is_parent_happy);

  const saveParent = async (changes) => {
    const ok = await run(saveParentDemoFeedback({ attemptId: attempt.retention_demo_attempt_id, ...changes }), 'Parent feedback saved');
    if (ok && Number(changes.is_parent_happy) === 0) setRemapping(true);
    return ok;
  };
  const remap = async (changes) => {
    const ok = await run(remapDemoAttempt({ attemptId: attempt.retention_demo_attempt_id, ...changes }), 'Tutor unmapped');
    if (ok) {
      setRemapping(false);
      navigate('../onboarding?stage=4');
    }
    return ok;
  };

  return (
    <Box>
      <AttemptHeader subject={subject} attempt={attempt} />
      <Stack gap={2.5}>
        <StageForm
          config={TUTOR_CONFIG}
          values={attempt}
          saving={saving}
          readOnly={!attempt.demo_datetime}
          readOnlyLabel="Schedule the demo first"
          saveLabel="Save tutor feedback"
          onSave={(changes) => run(saveTutorFeedback({ attemptId: attempt.retention_demo_attempt_id, ...changes }), 'Tutor feedback saved')}
          extraActions={tutorDone ? <SoftChip label="Recorded for the tutor" icon="solar:pen-bold" color={RT.muted} /> : null}
        />
        <StageForm
          config={PARENT_CONFIG}
          values={attempt}
          saving={saving}
          readOnly={!tutorDone}
          readOnlyLabel="Unlocks after the tutor's feedback"
          saveLabel="Save parent feedback"
          onSave={saveParent}
          extraActions={
            happy === 1 ? (
              <SoftChip label="Parent happy" icon="solar:like-bold" color={RT.success} />
            ) : happy === 0 ? (
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={saving}
                onClick={() => setRemapping(true)}
                startIcon={<Icon icon="solar:refresh-circle-bold" width={16} />}
              >
                Remap tutor
              </Button>
            ) : null
          }
        />
      </Stack>
      <RemapDialog
        open={remapping}
        tutorName={attempt.trainer_name}
        reasons={reasons}
        busy={saving}
        onClose={() => setRemapping(false)}
        onConfirm={remap}
      />
    </Box>
  );
}

export default function DemoAttemptStage({ stageNo }) {
  const { d, demo, saving, run } = useDemoData();

  if (demo.error) return <Alert severity="info">{demo.error.message}</Alert>;
  if (!d) return <EmptyState icon="svg-spinners:ring-resize" title="Loading…" compact />;

  const rows = d.subjects.map((s) => ({ subject: s, attempt: openAttempt(s) })).filter((r) => r.attempt);
  if (!rows.length) {
    return <EmptyState icon="solar:users-group-two-rounded-linear" title="Map a tutor first · stage 4" compact />;
  }

  return (
    <Stack gap={2.5} divider={<Divider flexItem />}>
      {rows.map(({ subject, attempt }) =>
        stageNo === 5 ? (
          <ScheduleBlock key={attempt.retention_demo_attempt_id} subject={subject} attempt={attempt} saving={saving} run={run} />
        ) : (
          <FeedbackBlock
            key={attempt.retention_demo_attempt_id}
            subject={subject}
            attempt={attempt}
            reasons={d.options?.retention_demo_rejection || []}
            saving={saving}
            run={run}
          />
        ),
      )}
    </Stack>
  );
}
